/**
 * Provides the `getURLParameter` utility function for retrieving the value of a specific query parameter from a given URL string, and its associated `GetURLParameterOptions` type.
 * @module getURLParameter
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Retrieves the value of a specific query parameter from a given URL string.
 *
 * **Behavior:**
 * - Takes a `parameter` key (string) and an optional `url` string.
 * - **Input validation:**
 *  - If the `parameter` key is empty, null, undefined, or contains only whitespace (checked via `isEmpty` and `trim`), it logs a warning and returns `null`.
 * - **URL handling:**
 *  - If the provided `url` is empty, null, or undefined (checked via `isEmpty`), it attempts to use the current browser's `window.location.href`.
 *  - If `window.location.href` is unavailable (e.g., not in a browser environment), it logs an error and returns `null`.
 * - **Parsing & extraction:**
 *  - Uses the standard `URL` constructor to parse the effective URL string. A base URL (`window.location.origin`) is provided if available to help parse relative URLs, but parsing might still fail for fundamentally invalid URL strings.
 *  - If URL parsing fails (throws an error), it logs the error and returns `null`.
 *  - Uses the `urlObject.searchParams.get(parameter)` method to retrieve the *first* value associated with the given parameter key.
 *  - URLSearchParams automatically handles URL decoding of keys and values (e.g., `%20` or `+` become spaces).
 * - Returns the found parameter value as a string.
 * - If the parameter key is not found in the query string, `searchParams.get` returns `null`, which is then returned by this function.
 * - If the parameter exists but has no value (e.g., `?key=`), it returns an empty string `''`.
 *
 * @since 3.3 Initial implementation.
 * @since 3.5 Converted into a utility function.
 * @param {string} parameter - The key (name) of the query parameter to retrieve. Cannot be empty or whitespace-only.
 * @param {string | null | undefined} [url=''] - The URL string to search within. If empty, null, or undefined, defaults to `window.location.href` in a browser context.
 * @return {string | null} The decoded value of the first occurrence of the parameter, an empty string (`''`) if the parameter exists with no value, or `null` if the parameter is not found, the parameter key is invalid, the URL is invalid/unparseable, or the environment lacks necessary context (e.g., `window.location` when no URL is provided).
 *
 * @example Importing the getURLParameter utility
 * ```typescript
 * import { getURLParameter } from '@automator/shared/utils/get-url-parameter';
 * ```
 *
 * @example Get existing parameter
 * Retrieves the value of 'param1'.
 * ```typescript
 * const url = 'https://automatorplugin.com/?param1=value1&param2=value2';
 * getURLParameter( 'param1', url );
 * // => 'value1'
 * ```
 *
 * @example Parameter not found
 * Returns null when the parameter key doesn't exist.
 * ```typescript
 * const url = 'https://automatorplugin.com/?param1=value1';
 * getURLParameter( 'param_missing', url );
 * // => null
 * ```
 *
 * @example Using default URL (window.location)
 * Retrieves 'param1' from the current page's URL.
 * ```typescript
 * // Assuming window.location.href is 'http://localhost/page?param1=windowValue'
 * getURLParameter( 'param1' ); // url argument is omitted
 * // => 'windowValue'
 * ```
 *
 * @example Handling encoded values
 * Automatically decodes percent-encoded values.
 * ```typescript
 * const url = 'https://automatorplugin.com?data=Hello%20World%21';
 * getURLParameter( 'data', url );
 * // => 'Hello World!'
 * ```
 *
 * @example Handling plus sign (+) encoding for spaces
 * Automatically decodes plus signs used as spaces in query strings.
 * ```typescript
 * const url = 'https://automatorplugin.com?query=search+term';
 * getURLParameter( 'query', url );
 * // => 'search term'
 * ```
 *
 * @example Duplicate parameters
 * Returns the value of the *first* occurrence if a parameter key appears multiple times.
 * ```typescript
 * const url = 'https://automatorplugin.com?filter=A&filter=B';
 * getURLParameter( 'filter', url );
 * // => 'A'
 * ```
 *
 * @example Parameter with empty value
 * Returns an empty string if the parameter exists but has no value after the equals sign.
 * ```typescript
 * const url = 'https://automatorplugin.com?key=&other=value';
 * getURLParameter( 'key', url );
 * // => ''
 * ```
 *
 * @example Handling invalid parameter key
 * Returns null and logs a warning if the parameter key is empty or whitespace.
 * ```typescript
 * const url = 'https://automatorplugin.com?a=1';
 * getURLParameter( '', url );
 * // => null (console.warn logs "Parameter key is empty or invalid.")
 * getURLParameter( '   ', url );
 * // => null (console.warn logs "Parameter key is empty or invalid.")
 * ```
 *
 * @example Handling invalid URL
 * Returns null and logs an error if the URL string cannot be parsed.
 * ```typescript
 * const url = '::not a valid url::';
 * getURLParameter( 'param', url );
 * // => null (console.error logs "Error parsing URL...")
 * ```
 *
 * @remarks
 * - This function relies on the standard `URL` and `URLSearchParams` browser APIs.
 * - When relying on the default URL (`window.location.href`), the function must be run in a browser context.
 * - It only retrieves the *first* value for a given parameter key if duplicates exist, which is standard behavior for `URLSearchParams.get()`.
 */
export function getURLParameter(parameter, url = '') {
    let currentUrl;
    // If the parameter key is empty, it cannot be retrieved.
    if (isEmpty(parameter) || parameter.trim() === '') {
        console.warn('getURLParameter: Parameter key is empty or invalid.');
        return null;
    }
    // Use window.location.href if the provided URL is empty, null, or undefined.
    if (isEmpty(url)) {
        // Ensure this code runs in a browser environment
        if (typeof window !== 'undefined' && window.location) {
            currentUrl = window.location.href;
        }
        else {
            // Not in a browser or window.location is not available
            console.error('getURLParameter: Cannot determine current URL outside of a browser environment when no URL is provided.');
            return null;
        }
    }
    else {
        currentUrl = url;
    }
    try {
        // Attempt to construct a URL object. This can throw an error if currentUrl is invalid.
        // If currentUrl is a relative path and no base is provided, it might also throw.
        const urlObject = new URL(currentUrl, !isEmpty(window) ? window.location.origin : undefined);
        // Get the parameter value from the URL's search parameters.
        return urlObject.searchParams.get(parameter);
    }
    catch (error) {
        console.error(`getURLParameter: Error parsing URL "${currentUrl}".`, error);
        return null;
    }
}
;
